// Format the normalized booking-terms envelope (listing `data.bookingTerms`,
// produced by the hubs' api/services/bookingTerms.js and backfilled across
// VTHUB + SHUB) into display-ready strings for the property detail page and the
// brochure. Pure/deterministic — no network, no FX, no markup. Every field is
// `null` when absent so callers can fall back to generic copy. Byte-identical
// across the two extranets, and in step with VT-FE's copy apart from three
// sentences (brought in line on 8 Oct 2026: band wording, which policies count
// as non-refundable, the day count, policies that arrive as a name only).

// Every code that appears in the data or that the backend bucketer
// (api/services/bookingTerms.js cancellationCodeFromWindows) can emit must have a
// label here, else buildCancellation falls back to an ugly capitalize (e.g. "Strict_60").
const CANCELLATION_LABELS = {
  flexible: 'Flexible',
  flex: 'Flexible',
  semi_flexible: 'Semi-Flexible',
  moderate: 'Moderate',
  semi_moderate: 'Semi-Moderate',
  firm: 'Firm',
  strict: 'Strict',
  strict_60: 'Strict (60 days)',
  super_strict: 'Super Strict',
  super_strict_30: 'Super Strict (30 days)',
  super_strict_60: 'Super Strict (60 days)',
  free: 'Free / Fully refundable',
  non_refundable: 'Non-refundable',
  nonRefundable: 'Non-refundable',
};

const cap = (s) =>
  typeof s === 'string' && s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// A band covers cancellations made `fromDays` or more, and fewer than `toDays`, days
// before arrival; `toDays: null` has no upper end, i.e. "fromDays days or more before
// arrival" (the backend reads it the same way: services/cancellationRefund.js
// partnerRefundPctAt). The open-ended band used to be worded "Within N days of
// arrival", the opposite of what it means, on every villa that has bands.
const isEmptyBand = (w) => {
  if (!w || w.toDays == null) return false;
  const from = Number(w.fromDays);
  const to = Number(w.toDays);
  return Number.isFinite(from) && Number.isFinite(to) && to <= from;
};

const days = (n) => `${n} day${n === 1 ? '' : 's'}`;

// Bands for a policy that arrives as a NAME only (no day bands of its own). KEEP IN STEP
// with VT-Backend services/cancellationRefund.js CODE_POLICY_TABLE: that table is what a
// refund is worked out from when such a booking is cancelled. The date-aware line treated
// every name-only policy as fully refundable, so inside a named policy's own last days it
// said "about 50% refundable" where the backend refunds nothing.
const CODE_POLICY_BANDS = {
  flexible: [{ fromDays: 0, toDays: 1, penaltyPct: 100 }, { fromDays: 1, toDays: null, penaltyPct: 0 }],
  moderate: [{ fromDays: 0, toDays: 5, penaltyPct: 100 }, { fromDays: 5, toDays: null, penaltyPct: 0 }],
  firm: [{ fromDays: 0, toDays: 30, penaltyPct: 100 }, { fromDays: 30, toDays: null, penaltyPct: 0 }],
  strict: [{ fromDays: 0, toDays: 7, penaltyPct: 100 }, { fromDays: 7, toDays: 30, penaltyPct: 50 }, { fromDays: 30, toDays: null, penaltyPct: 0 }],
  non_refundable: [{ fromDays: 0, toDays: null, penaltyPct: 100 }],
};
const normalizeCode = (code) => String(code || '').trim().toLowerCase().replace(/[\s-]+/g, '_');

// The bands the backend will judge a cancellation by: the policy's own, else the named
// policy's, else none (a policy it has no bands for is treated as refundable in full).
export function bandsOf(cancellation) {
  if (!cancellation) return null;
  if (Array.isArray(cancellation.windows) && cancellation.windows.length) return cancellation.windows;
  if (cancellation.code) return CODE_POLICY_BANDS[normalizeCode(cancellation.code)] || null;
  return null;
}

function fmtWindow(w) {
  if (!w) return null;
  if (isEmptyBand(w)) return null; // covers no day at all (0–0): nothing to say
  const pct = Number(w.penaltyPct);
  const from = Number(w.fromDays);
  const to = w.toDays == null ? null : Number(w.toDays);
  const pctPart = !Number.isFinite(pct) ? 'penalty applies' : pct === 0 ? 'no penalty' : `${pct}% penalty`;
  if (Number.isFinite(from) && to != null && Number.isFinite(to)) {
    return from === 0
      ? `Less than ${days(to)} before arrival: ${pctPart}`
      : `${to}–${from} days before arrival: ${pctPart}`;
  }
  if (Number.isFinite(from)) {
    return from === 0
      ? `At any time before arrival: ${pctPart}`
      : `${days(from)} or more before arrival: ${pctPart}`;
  }
  return pctPart;
}

function buildCancellation(c) {
  if (!c) return { text: null, windows: null };
  const windows =
    Array.isArray(c.windows) && c.windows.length
      ? c.windows.map(fmtWindow).filter(Boolean).map((label) => ({ label }))
      : null;
  // Prefer human text (DH), else the windows summary, else the policy-code label.
  let text = typeof c.text === 'string' && c.text.trim() ? c.text.trim() : null;
  if (!text && windows) text = windows.map((w) => w.label).join('; ');
  if (!text && c.code) text = CANCELLATION_LABELS[c.code] || cap(String(c.code));
  return { text: text || null, windows };
}

function buildDeposit(d) {
  if (!d || d.amount == null || !(Number(d.amount) > 0)) return null;
  const amount = Number(d.amount);
  const currency = d.currency || '';
  const kind = d.type === 'booking' ? 'Booking deposit' : 'Security deposit';
  const refundable =
    d.refundable === true
      ? ' (refundable)'
      : d.refundable === false
      ? ' (non-refundable)'
      : '';
  const amountText = `${amount.toLocaleString()} ${currency}`.trim();
  return {
    text: `${kind}: ${amountText}${refundable}`,
    amount,
    currency,
    type: d.type || null,
    refundable: d.refundable,
  };
}

function fmtScheduleRow(row) {
  if (!row) return null;
  const isPct =
    row.chargeType === 'PERCENTAGE' ||
    row.chargeType === 'percent' ||
    row.chargeType === 'PERCENT';
  const amt =
    row.amount != null ? (isPct ? `${row.amount}%` : `${row.amount}`) : null;
  const whenVal = row?.when?.value;
  const whenCtx = row?.when?.context;
  let when = '';
  if (whenCtx === 'AT_RESERVATION' || whenCtx === 'BOOKING' || whenVal === 0) {
    when = 'at booking';
  } else if (whenVal != null && whenCtx) {
    const n = Number(whenVal);
    when = `${Math.abs(n)} days ${n < 0 ? 'before' : 'after'} ${String(whenCtx)
      .toLowerCase()
      .replace(/_/g, ' ')}`;
  } else if (whenCtx) {
    when = String(whenCtx).toLowerCase().replace(/_/g, ' ');
  }
  const label = [amt, when].filter(Boolean).join(' ');
  return label ? { label: cap(label) } : null;
}

const EMPTY = {
  hasAny: false,
  cancellationText: null,
  cancellationWindows: null,
  depositText: null,
  deposit: null,
  paymentSchedule: null,
  checkIn: null,
  checkOut: null,
  checkInEnd: null,
  minNights: null,
  maxNights: null,
  leadDays: null,
  houseRulesText: null,
};

export function formatBookingTerms(bt) {
  if (!bt || typeof bt !== 'object') return { ...EMPTY };
  const cancellation = buildCancellation(bt.cancellation);
  const deposit = buildDeposit(bt.deposit);
  const schedule =
    Array.isArray(bt.paymentSchedule) && bt.paymentSchedule.length
      ? bt.paymentSchedule.map(fmtScheduleRow).filter(Boolean)
      : null;
  const houseRulesText =
    typeof bt.houseRules === 'string' && bt.houseRules.trim()
      ? bt.houseRules.trim()
      : null;
  const out = {
    cancellationText: cancellation.text,
    cancellationWindows: cancellation.windows,
    depositText: deposit ? deposit.text : null,
    deposit,
    paymentSchedule: schedule && schedule.length ? schedule : null,
    checkIn: bt.checkIn || null,
    checkOut: bt.checkOut || null,
    checkInEnd: bt.checkInEnd || null,
    minNights: bt.minNights == null ? null : bt.minNights,
    maxNights: bt.maxNights == null ? null : bt.maxNights,
    leadDays: bt.leadDays == null ? null : bt.leadDays,
    houseRulesText,
  };
  out.hasAny = !!(
    out.cancellationText ||
    out.depositText ||
    out.paymentSchedule ||
    out.houseRulesText ||
    out.checkIn ||
    out.checkOut
  );
  return out;
}

// True when the partner policy refunds nothing at any point. THIS IS WHAT IS SAVED ON
// A BOOKING (cancellationSnapshot.nonRefundable, in PropertyReserve and in
// PartnersBookingpal/ReservationDemo), and the backend then
// refunds nothing whatever the bands say — so it must not say yes to a policy that
// refunds.
//
// When a policy has day bands, the bands decide. The words in its name or text decide
// only when it has none. It used to read the text first: the standard policy of 1,097
// live villas (every Villas in St Barth villa, Invenio Homes, some others) says "full
// refund up to 30 days before arrival; within 30 days a minimum of 50% is
// non-refundable", and that one word made the whole policy non-refundable — on the page,
// and on the booking.
export function detectNonRefundable(cancellation) {
  if (!cancellation) return false;
  const w = cancellation.windows;
  if (Array.isArray(w) && w.length) return w.every((x) => Number(x.penaltyPct) >= 100);
  const hay = `${cancellation.code || ''} ${cancellation.text || ''}`;
  return /non[\s-]?refundable/i.test(hay);
}

// FOR DISPLAY: does this policy refund nothing at any lead time? detectNonRefundable
// above misses the shape [[0,0,0],[0,null,100]] (an empty band, then 100% from the day
// of booking): its first band is not a 100% one, so it said "refundable" while every
// real day falls in the 100% band and the backend refunds nothing. This looks at the
// bands that cover at least one day, and at a named policy's bands (bandsOf). For that
// shape the saved flag stays as it was; the backend reaches 0% through the bands.
export function refundsNothing(cancellation) {
  if (detectNonRefundable(cancellation)) return true;
  const w = bandsOf(cancellation);
  if (!Array.isArray(w)) return false;
  const real = w.filter((x) => x && !isEmptyBand(x));
  return real.length > 0 && real.every((x) => Number(x.penaltyPct) >= 100);
}

// A calendar date as a day number, read the same in every time zone.
// `new Date('2026-12-07')` is midnight UTC, which is still 6 December for a browser in
// the Americas, so the day count came out one short there and, on the edge day, the
// line named the wrong band. A bare 'YYYY-MM-DD' (what the date pickers store) is taken
// as that calendar date; anything else, including a value with a time in it, falls back
// to the date the browser shows for it, as before.
export function calendarDay(value) {
  if (value == null || value === '') return null;
  const m = typeof value === 'string' ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim()) : null;
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

// Guest-facing Smiling House cancellation policy copy: partner terms for
// cancellations 30+ days before check-in, plus the standard "within 30 days,
// min 50% of the accommodation total is non-refundable" condition; non-refundable
// properties get a distinct single line. Returns { nonRefundable, lines[],
// partnerText, windows } so callers render + badge consistently.
export function smilingHouseCancellationCopy(bt) {
  const t = formatBookingTerms(bt);
  const nonRefundable = refundsNothing(bt && bt.cancellation);
  if (nonRefundable) {
    return {
      nonRefundable: true,
      lines: ['This property is non-refundable — no refund is available after booking.'],
      partnerText: t.cancellationText,
      windows: t.cancellationWindows,
    };
  }
  return {
    nonRefundable: false,
    lines: [
      t.cancellationText
        ? `Cancellations 30 or more days before check-in follow this property's terms: ${t.cancellationText.replace(/[.\s]+$/, '')}.`
        : "Cancellations 30 or more days before check-in follow this property's own cancellation terms.",
      'For cancellations less than 30 days before check-in, a minimum of 50% of the accommodation total is non-refundable (or the property’s terms, if stricter).',
    ],
    partnerText: t.cancellationText,
    windows: t.cancellationWindows,
  };
}

// Date-aware cancellation outcome for a SELECTED check-in date (cancel moment =
// now). Mirrors the backend refund engine's window-pick + SH overlay so a guest
// sees which condition applies for their dates. Indicative for display only.
// A policy that arrives as a name only is judged by that name's bands (bandsOf),
// as the backend does; a name it has no bands for counts as refundable in full, and
// the 50% floor still caps within 30 days. Returns null when there is no valid date.
export function cancellationForDates(bt, checkInDate) {
  if (!checkInDate) return null;
  const checkInDay = calendarDay(checkInDate);
  if (checkInDay == null) return null;
  const now = new Date();
  const daysOut = Math.round(
    (checkInDay - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000
  );
  const cx = bt && bt.cancellation;
  const nonRefundable = refundsNothing(cx);
  let partnerPct = 100;
  const bands = bandsOf(cx);
  if (nonRefundable) partnerPct = 0;
  else if (bands) {
    for (const w of bands) {
      const from = Number(w.fromDays);
      const to = w.toDays == null ? Infinity : Number(w.toDays);
      if (daysOut >= from && daysOut < to) {
        partnerPct = Math.max(0, Math.min(100, 100 - Number(w.penaltyPct)));
        break;
      }
    }
  }
  const guestPct = daysOut >= 30 ? partnerPct : Math.min(partnerPct, 50);
  return {
    daysOut,
    guestPct,
    nonRefundable,
    within30: daysOut < 30,
    message: nonRefundable
      ? 'This booking is non-refundable — no refund applies for your selected dates.'
      : daysOut >= 30
        ? `Your check-in is ${daysOut} days away: the property's own cancellation terms apply (currently about ${guestPct}% refundable).`
        : `Your check-in is ${daysOut} days away (under 30): a minimum of 50% of the accommodation total is non-refundable — currently about ${guestPct}% refundable.`,
  };
}

export default formatBookingTerms;
